import { Box, IconButton, Typography } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { DateTime } from "luxon";
import { keyframes } from "@emotion/react";
import { useAppDispatch, useAppSelector } from "../../Redux/hooks";
import {
  dismissMaintenance,
  MAINTENANCE_DISMISSED_STORAGE_KEY,
} from "../../Redux/reducers/maintenanceSlice";

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-100%); }
  to   { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const formatWindow = (startsAt: string, endsAt: string) => {
  const start = DateTime.fromISO(startsAt);
  const end = DateTime.fromISO(endsAt);
  if (!start.isValid || !end.isValid) return "";
  const sameDay = start.hasSame(end, "day");
  const startFmt = start.toLocaleString(DateTime.DATETIME_MED);
  const endFmt = sameDay
    ? end.toLocaleString(DateTime.TIME_SIMPLE)
    : end.toLocaleString(DateTime.DATETIME_MED);
  const zone = start.offsetNameShort || "";
  return zone ? `${startFmt} – ${endFmt} ${zone}` : `${startFmt} – ${endFmt}`;
};

const MaintenanceBanner = () => {
  const dispatch = useAppDispatch();
  const window = useAppSelector((state) => state.maintenance.window);
  const dismissed = useAppSelector((state) => state.maintenance.dismissed);

  if (!window || dismissed) return null;

  const now = DateTime.now();
  const end = DateTime.fromISO(window.endsAt);
  if (!end.isValid || now >= end) return null;

  const handleDismiss = () => {
    localStorage.setItem(MAINTENANCE_DISMISSED_STORAGE_KEY, "true");
    dispatch(dismissMaintenance());
  };

  return (
    <Box
      role="alert"
      aria-live="assertive"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: (theme) => theme.zIndex.modal + 1,
        color: "#1F1300",
        background:
          "linear-gradient(90deg, #FBBF24 0%, #F59E0B 50%, #F97316 100%)",
        boxShadow: "0 6px 20px rgba(249, 115, 22, 0.35)",
        borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
        overflow: "hidden",
        animation: `${slideDown} 260ms cubic-bezier(0.16, 1, 0.3, 1)`,
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.35) 50%, transparent 100%)",
          animation: `${shimmer} 3.5s ease-in-out infinite`,
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: { xs: 1.25, sm: 2 },
          px: { xs: 2, sm: 3 },
          py: { xs: 1, sm: 1.25 },
          maxWidth: 1400,
          mx: "auto",
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 32,
            height: 32,
            borderRadius: "50%",
            bgcolor: "rgba(0, 0, 0, 0.12)",
          }}
        >
          <WarningAmberRoundedIcon sx={{ fontSize: 20, color: "#1F1300" }} />
        </Box>
        <Box
          sx={{
            minWidth: 0,
            flex: 1,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            gap: { xs: 0.25, sm: 1.5 },
          }}
        >
          <Typography
            component="span"
            sx={{
              flexShrink: 0,
              fontSize: { xs: 13, sm: 14 },
              fontWeight: 800,
              letterSpacing: 0.4,
              textTransform: "uppercase",
              whiteSpace: "nowrap",
            }}
          >
            Scheduled maintenance
          </Typography>
          <Box
            component="span"
            aria-hidden
            sx={{
              display: { xs: "none", sm: "inline-block" },
              width: 4,
              height: 4,
              borderRadius: "50%",
              bgcolor: "rgba(31, 19, 0, 0.5)",
              flexShrink: 0,
            }}
          />
          <Typography
            component="span"
            sx={{
              flexShrink: 0,
              fontSize: { xs: 12.5, sm: 13.5 },
              fontWeight: 600,
              color: "rgba(31, 19, 0, 0.85)",
              whiteSpace: "nowrap",
            }}
          >
            {formatWindow(window.startsAt, window.endsAt)}
          </Typography>
          <Typography
            component="span"
            sx={{
              fontSize: { xs: 12.5, sm: 13.5 },
              fontWeight: 500,
              color: "rgba(31, 19, 0, 0.85)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
              flex: 1,
              "&::before": {
                content: { xs: '""', sm: '"— "' },
              },
            }}
          >
            {window.message}
          </Typography>
        </Box>
        <IconButton
          onClick={handleDismiss}
          size="small"
          aria-label="Dismiss maintenance notice"
          sx={{
            flexShrink: 0,
            color: "rgba(31, 19, 0, 0.8)",
            "&:hover": {
              color: "#1F1300",
              bgcolor: "rgba(0, 0, 0, 0.08)",
            },
          }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

export default MaintenanceBanner;
